import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useGetISOCountries } from 'utils/hooks/data/useGetISOCountries';
import { JobFamilyPicker } from 'common/JobFamilyPicker';
import { JobProfileCreateModelInput, JobFamilyModel } from '@utmost.co/enterprise-models';
import { Dialog, Input, Select, Button } from '@beeline.com/bp-design-system';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { UseState } from 'src/types';
import { byCountryName, useJobProfileData } from './useJobProfileData';
import { EngagementJobProfile } from './jobProfiles.mutations';
import { useGetAllJobProfiles } from './useGetAllJobProfiles';
import { useDisabledJobProfileFields } from './useDisabledJobProfileFields';
import { useJobProfilesIntl } from './translations/useJobProfilesIntl';

const prepareJobProfileCreateInput = (
  jobProfile: Partial<EngagementJobProfile>
): DeepPartial<JobProfileCreateModelInput> => ({
  code: jobProfile?.code ?? 'Default',
  status: 'ACTIVE',
  name: jobProfile?.name ?? '',
  jobFamily: jobProfile?.jobFamily ? { uid: jobProfile?.jobFamily?.uid } : undefined,
  classifications: jobProfile?.classifications ? jobProfile.classifications.map((c) => ({ uid: c.uid })) : [],
  mappedExportJobProfile: jobProfile?.mappedExportJobProfile
    ? { uid: jobProfile.mappedExportJobProfile.uid }
    : undefined,
  summary: jobProfile?.summary ?? '',
  countries: jobProfile?.countries || []
});

interface Props {
  addModalOpen: boolean;
  setAddModal: UseState<boolean>;
  editableResource?: EngagementJobProfile | null;
  onComplete: () => void;
}

const JobProfileModalComponent: FC<React.PropsWithChildren<Props>> = ({
  addModalOpen,
  onComplete,
  setAddModal,
  editableResource
}) => {
  const intl = useJobProfilesIntl();
  const { showError } = useSnackbar();
  const { getCountries, isoCountries } = useGetISOCountries();
  const sortedCountries = useMemo(() => isoCountries.sort(byCountryName), [isoCountries]);
  const [jobProfile, setJobProfile] = useState<Partial<EngagementJobProfile>>({ ...editableResource });
  const { create, update, loading: loadingJobProfile } = useJobProfileData();
  const { jobProfiles, classifications, loadingJobProfiles, loadJobProfiles, loadClassifications } =
    useGetAllJobProfiles();
  const jobProfileCountries = useMemo(
    () => jobProfile?.countries?.map((c) => ({ label: c.name, value: c.code })) || [],
    [jobProfile]
  );

  useEffect(() => {
    if (addModalOpen) {
      getCountries();
    }
  }, [addModalOpen, getCountries]);

  const handleClose = useCallback(
    (callComplete: boolean) => {
      if (callComplete) onComplete();
      setAddModal(false);
      setJobProfile({});
    },
    [setAddModal, onComplete]
  );

  const handleCreateJobProfile = useCallback(async () => {
    try {
      await create({
        jobProfile: prepareJobProfileCreateInput(jobProfile)
      });
      handleClose(true);
    } catch {}
  }, [handleClose, create, jobProfile]);

  const handleUpdateJobProfile = useCallback(async () => {
    try {
      await update({
        jobProfile: {
          uid: jobProfile.uid!,
          ...prepareJobProfileCreateInput(jobProfile)
        }
      });
      handleClose(true);
    } catch (err) {
      showError((err as any).toString());
    }
  }, [handleClose, jobProfile, showError, update]);

  const isFormValid = useMemo(() => jobProfile?.name && jobProfile?.code, [jobProfile]);
  const disabledFieldResult = useDisabledJobProfileFields({ loading: loadingJobProfile, editableResource });

  return (
    <Dialog
      title={editableResource ? intl.updateJobProfile : intl.createNewJobProfile}
      subTitle={editableResource ? intl.updateJobProfileDetails : intl.enterDetailsToCreateJobProfile}
      open={addModalOpen}
      onClose={() => handleClose(false)}
      dataTest="manage-job-profile-model"
      width="sm"
      show={{ cancelButton: true }}
      actions={[
        <Button
          disabled={!isFormValid}
          loading={loadingJobProfile}
          dataTest="job-profile__dialog__save"
          variant="contained"
          color="primary"
          onClick={editableResource ? handleUpdateJobProfile : handleCreateJobProfile}
          key="confirm"
        >
          {intl.save}
        </Button>
      ]}
    >
      <StyledFirstLine key="job-code">
        <Input
          dataTest="job-profile__form__code"
          required
          disabled={disabledFieldResult.jobCode}
          label={intl.jobCode}
          fullWidth
          autoFocus
          value={jobProfile?.code || ''}
          onChange={(jobCode) => setJobProfile({ ...jobProfile, code: jobCode.target.value })}
        />
      </StyledFirstLine>
      <StyledLine key="mappedExportJobProfile">
        <StyledMuiSelectWrapper $isDisabled={disabledFieldResult.mappedExports}>
          <Select<string>
            fullWidth
            autoComplete
            loading={loadingJobProfiles}
            disabled={disabledFieldResult.mappedExports}
            label={intl.mappedExportJobProfile}
            onOpen={loadJobProfiles}
            valueOptions={{
              value: jobProfile?.mappedExportJobProfile?.uid ?? '',
              label: jobProfile?.mappedExportJobProfile?.name ?? ''
            }}
            dataTest="job-profile__form__mapped-export-job-profile"
            options={jobProfiles?.filter((p) => p.externalId).map((p) => ({ label: p.name, value: p.uid }))}
            onOptionChange={(option) => {
              // TS type guard
              if (option && !Array.isArray(option)) {
                setJobProfile({
                  ...jobProfile,
                  name: option.label,
                  mappedExportJobProfile: jobProfiles?.find((item) => item.uid === option.value) as EngagementJobProfile
                });
              } else if (!option) {
                setJobProfile({
                  ...jobProfile,
                  mappedExportJobProfile: null
                });
              }
            }}
          />
        </StyledMuiSelectWrapper>
      </StyledLine>
      <StyledLine key="title">
        <Input
          required
          label={intl.title}
          disabled={disabledFieldResult.name}
          dataTest="job-profile__form__title"
          fullWidth
          value={jobProfile?.name || ''}
          onChange={(name) =>
            setJobProfile({
              ...jobProfile,
              name: name.target.value
            })
          }
        />
      </StyledLine>
      <StyledLine key="jobFamily">
        <StyledMuiSelectWrapper $isDisabled={disabledFieldResult.jobFamily}>
          <JobFamilyPicker
            disabled={disabledFieldResult.jobFamily}
            label={intl.jobFamily}
            value={jobProfile.jobFamily?.uid}
            dataTest="job-profile__form__job-family"
            onChange={(value) => {
              setJobProfile((prev) => ({
                ...prev,
                jobFamily: {
                  uid: value
                } as JobFamilyModel
              }));
            }}
          />
        </StyledMuiSelectWrapper>
      </StyledLine>
      <StyledLine key="countries">
        <StyledMuiSelectWrapper $isDisabled={disabledFieldResult.countries}>
          <Select<string, true>
            fullWidth
            multiple
            autoComplete
            disabled={disabledFieldResult.countries}
            label={intl.country}
            onOpen={() => getCountries()}
            valueOptions={jobProfileCountries}
            dataTest="job-profile__form__countries"
            className={sortedCountries?.length ? 'data-loaded' : undefined}
            options={sortedCountries?.map((c) => ({ label: c.name, value: c.code })) || []}
            onOptionChange={(options) => {
              // TS type guard
              if (options && Array.isArray(options)) {
                const selected = sortedCountries?.filter((c) => options.find((o) => o.value === c.code));
                setJobProfile({
                  ...jobProfile,
                  countries: selected
                });
              }
            }}
          />
        </StyledMuiSelectWrapper>
      </StyledLine>
      <StyledLine key="classifications">
        <StyledMuiSelectWrapper $isDisabled={disabledFieldResult.classifications}>
          <Select<string, true>
            fullWidth
            multiple
            disabled={disabledFieldResult.classifications}
            label={intl.classifications}
            onOpen={() => loadClassifications()}
            valueOptions={jobProfile?.classifications?.map((c) => ({ label: c.name, value: c.uid })) || []}
            dataTest="job-profile__form__classifications"
            options={classifications?.map((c) => ({ label: c.name, value: c.uid })) || []}
            onOptionChange={(options) => {
              // TS type guard
              if (options && Array.isArray(options) && classifications) {
                const selected = classifications.filter((c) => options.find((o) => o.value === c.uid));
                setJobProfile({
                  ...jobProfile,
                  classifications: selected
                });
              }
            }}
          />
        </StyledMuiSelectWrapper>
      </StyledLine>
      <StyledLine key="Summary">
        <Input
          label={intl.summary}
          disabled={disabledFieldResult.summary}
          dataTest="job-profile__form__summary"
          fullWidth
          value={jobProfile?.summary || ''}
          multiline
          minRows={3}
          autoHeight
          onChange={(summary) =>
            setJobProfile({
              ...jobProfile,
              summary: summary.target.value
            })
          }
        />
      </StyledLine>
    </Dialog>
  );
};

const StyledLine = styled.div`
  margin: 1rem 0;
  min-height: 35px;
`;

const StyledFirstLine = styled(StyledLine)`
  margin-top: 0;
`;

const StyledMuiSelectWrapper = styled.div<{
  $isDisabled?: boolean;
}>`
  .MuiAutocomplete-root,
  .MuiInputBase-root {
    background-color: ${({ $isDisabled }) => ($isDisabled ? '#f0f1f2' : 'inherit')};
  }
`;
export const JobProfileModal = memo(JobProfileModalComponent);
